import { Component } from 'react';
import { Cell } from '@table-library/react-table-library/table';
import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import Loading from '../../../../components/Loading';
import { contributorColumns } from '../../GscTools/columns';
import { reportsPosition } from '../../GscTools/metrics';
import { formatPeriod } from '../../GscTools/period';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { reasonColumn } from '../keys';

/**
 * Block 02 — the queries worth a look under the criteria in force.
 *
 * These are rows Search Console returned for the window, read against thresholds in the browser;
 * no request asked for "striking distance" and none could. What the table shows about a row is
 * therefore exactly what the API said about it, plus a column stating which criteria put it here.
 *
 * Impressions are impressions, not search volume, and average position is an impression-weighted
 * mean over the window, not a rank the query holds now. The note under the table says both,
 * because a queue of "almost there" queries invites precisely those two readings.
 */
class Candidates extends Component {
    constructor(props) {
        super(props);
        this.searchFunction = this.searchFunction.bind(this);
        this.actionColumn = this.actionColumn.bind(this);
        this.handleRowClick = this.handleRowClick.bind(this);
    }

    /**
     * Clicking the row does what its button does, including toggling back off.
     *
     * The same handler rather than a second path to the same place, so the two can never drift:
     * whatever the button means by "open this query", the row means too.
     */
    handleRowClick(item) {
        const { selectedQuery, onSelect } = this.props;
        onSelect(item.key === selectedQuery ? null : item.key);
    }

    searchFunction(data, search) {
        if (!search) return data;
        const term = search.toLowerCase();
        return data.filter((item) => item.key?.toLowerCase().includes(term));
    }

    /** The button that loads a query's page contributors into the block below. */
    actionColumn() {
        const { t, selectedQuery, onSelect } = this.props;

        return {
            label: t('tools.search_opportunities.candidates.pages_column'),
            key: 'pages',
            data: 'key',
            sortType: (array) => array,
            cellSize: 'minmax(130px, 150px)',
            Component: Cell,
            renderCell: ({ item, csvDownload }) => {
                if (csvDownload) return '';

                const picked = item.key === selectedQuery;

                return (
                    <div className="d-flex">
                        <button
                            type="button"
                            className={`table-action-main-btn ${picked ? 'is-picked' : ''}`}
                            aria-pressed={picked}
                            onClick={() => onSelect(picked ? null : item.key)}
                        >
                            {picked && <i className="icon-check-icon" aria-hidden="true" />}
                            {picked
                                ? t('tools.search_opportunities.candidates.pages_showing')
                                : t('tools.search_opportunities.candidates.pages_action')}
                        </button>
                    </div>
                );
            },
        };
    }

    render() {
        const {
            t,
            scope,
            status,
            error,
            rows,
            retrievedRows,
            selectedQuery,
            stopReason,
            loadingMore,
            onLoadMore,
        } = this.props;

        const columns = [
            ...contributorColumns(t, {
                hasPosition: reportsPosition(scope.searchType),
                keyLabel: t('tools.gsc.columns.query'),
                isUrl: false,
            }),
            reasonColumn(t),
        ];
        const tableColumns = [...columns, this.actionColumn()];

        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-header d-flex justify-content-between">
                    <div>
                        <p className="card-title">
                            {t('tools.search_opportunities.candidates.title')}
                        </p>
                        <p className="gsc-tool-sub">
                            {t('tools.search_opportunities.candidates.description')} ·{' '}
                            {formatPeriod(scope.current)}
                        </p>
                        <p className="gsc-tool-rows-hint">
                            <i className="icon-info" aria-hidden="true" />
                            {t('tools.search_opportunities.candidates.select_hint')}
                        </p>
                    </div>
                </div>

                <div className="card-body">
                    {(status === 'idle' || status === 'loading') && <Loading padding={20} />}

                    {status === 'error' && (
                        <div className="gsc-tool-banner error">
                            {error ?? t('global.something_went_wrong')}
                        </div>
                    )}

                    {status === 'done' && (
                        <>
                            {!!error && <div className="gsc-tool-banner error">{error}</div>}

                            <DataTable
                                nodes={rows}
                                COLUMNS={tableColumns}
                                customColumnsForExport={columns}
                                sortFns={getSortKeys(tableColumns)}
                                additionalStyles={setAdditionalStyles(tableColumns)}
                                searchFunction={this.searchFunction}
                                fileName={t('tools.search_opportunities.candidates.title')}
                                customEmptyMessage={t(
                                    'tools.search_opportunities.candidates.empty',
                                )}
                                customContainerStyles="gsc-tool-key-table"
                                rowClassName={(item) =>
                                    item.key === selectedQuery ? 'dt-row-picked' : ''
                                }
                                onRowClick={this.handleRowClick}
                                loadMore={onLoadMore}
                                loadMoreLoading={loadingMore}
                                showLoadMoreBtn={stopReason === 'more_available'}
                            />

                            <p className="gsc-tool-table-note">
                                {t('tools.search_opportunities.candidates.universe', {
                                    retrieved: retrievedRows,
                                    candidates: rows.length,
                                })}{' '}
                                {t(`tools.gsc.stop.${stopReason}`)}
                            </p>
                            <p className="gsc-tool-table-note">
                                {t('tools.search_opportunities.candidates.not_volume')}
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(Candidates);
